import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { SurfaceCard } from '@client/components/_ui/display/SurfaceCard';
import { Space } from '@client/components/_ui/layout/Space';
import { CopyButton } from '@client/components/lodgement/shared/CopyButton';
import { useIsLiteOrg } from '@client/hooks/business/org/useIsLiteOrg';
import { useOrg } from '@client/hooks/business/org/useOrg';
import { generateHref } from '@shared/utils/core/navigation';
import { createFileRoute } from '@tanstack/react-router';
import { Input } from 'antd';
import { ExternalLink, Link2, Settings2 } from 'lucide-react';
import { ComplaintAcknowledgmentToggle } from '../ComplaintAcknowledgmentToggle';

export const Route = createFileRoute('/console/$orgId/configuration/compliance/complaints/general/lodgement')({
    staticData: {
        metadata: {
            title: 'Lodgement and acknowledgements',
        },
        ui: {
            icon: Settings2,
            title: 'Complaint settings',
            subtitle: 'Lodgement link, acknowledgements and timeline',
        },
    },
    component: ComplaintLodgement,
});

function ComplaintLodgement() {
    const { id: orgId } = useOrg();
    // Curium Lite (CUR-379): acknowledgement email/SMS is removed on the Lite tier (not just
    // locked) — the server never sends them for Lite orgs, so the toggle is hidden entirely.
    const isLiteOrg = useIsLiteOrg();
    const publicLink = generateHref({
        to: '/public/$orgId/complaint/new',
        params: { orgId },
        baseUrl: window.location.origin,
    });

    return (
        <PageController route={Route}>
            <Space orientation="vertical" size="middle" className="w-full">
                <SurfaceCard icon={Link2} title="Public lodgement form">
                    <div className="flex items-center gap-2">
                        <Input
                            readOnly
                            value={publicLink}
                            aria-label="Public complaint form link"
                            className="font-mono"
                        />
                        <CopyButton
                            value={publicLink}
                            label="Copy link"
                            tooltip="Copy the link"
                            size="middle"
                            type="secondary"
                        />
                        <Button.Link
                            to="/public/$orgId/complaint/new"
                            params={{ orgId }}
                            target="_blank"
                            variant="secondary"
                            icon={<ExternalLink />}
                            className="shrink-0"
                        >
                            Open
                        </Button.Link>
                    </div>
                    <p className="text-muted-foreground text-xs">
                        Share this link or put it on your website. Complaints lodged through it arrive as new
                        complaints.
                    </p>
                </SurfaceCard>
                {!isLiteOrg && <ComplaintAcknowledgmentToggle />}
            </Space>
        </PageController>
    );
}
